import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckOutlined, TruckOutlined, UploadOutlined } from '@ant-design/icons';
import { Empty, Timeline, Typography } from 'src/design-system/components/atoms';
import { customFormatDate } from 'src/utils/utils';
import { TrackingConsignment, TrackingEvent } from '../GlobalTrack.types';
import {
  buildLocationText,
  isDeliveredStatus,
  prettifyStatus,
  resolveEventDescription,
  resolveFailureReasonLabel,
} from '../GlobalTrack.utils';
import { useTrackingTimelineStyles } from '../GlobalTrack.styles';

interface IProps {
  consignment: TrackingConsignment;
}

const DATE_FORMAT = 'DD/MM/YYYY';
const TIME_FORMAT = 'hh:mm A';

const TrackingTimeline = (props: IProps) => {
  const { consignment } = props;
  const { t, i18n } = useTranslation();
  const classes = useTrackingTimelineStyles();

  const renderDot = (isGreen: boolean, icon: React.ReactNode) => (
    <span className={`${classes.dot} ${isGreen ? classes.dotDone : classes.dotTransit}`}>
      {icon}
    </span>
  );

  const renderLabel = (timestamp: TrackingEvent['timestamp']) => (
    <div className={classes.label}>
      <Typography.Text className={classes.date}>
        {timestamp ? customFormatDate(timestamp, DATE_FORMAT) : ''}
      </Typography.Text>
      <Typography.Text className={classes.time}>
        {timestamp ? customFormatDate(timestamp, TIME_FORMAT) : ''}
      </Typography.Text>
    </div>
  );

  const renderFailureReason = (event: TrackingEvent) => {
    const reasonLabel = resolveFailureReasonLabel(event);
    if (!reasonLabel) {
      return null;
    }
    return (
      <Typography.Text type="secondary">
        {t('Failure Reason')}: {t(reasonLabel)}
      </Typography.Text>
    );
  };

  const renderReceiverInfo = (statusInternal?: string) => {
    if (statusInternal !== 'delivered' || !consignment.receiverName) {
      return null;
    }
    return (
      <div className={classes.receiverInfo}>
        <Typography.Text type="secondary">
          {t('Receiver Name')}: <Typography.Text strong>{consignment.receiverName}</Typography.Text>
        </Typography.Text>
        {consignment.receiverRelation ? (
          <Typography.Text type="secondary">
            {t('Receiver Relation')}:{' '}
            <Typography.Text strong>{consignment.receiverRelation}</Typography.Text>
          </Typography.Text>
        ) : null}
      </div>
    );
  };

  const hasGrouped =
    Boolean(consignment.groupedStatusBy) &&
    Array.isArray(consignment.groupedTrackingHistory) &&
    consignment.groupedTrackingHistory.length > 0;

  if (hasGrouped) {
    const { groupedStatusBy } = consignment;
    const groups = [...(consignment.groupedTrackingHistory || [])]
      .reverse()
      .map((group) => [...group].reverse().filter((event) => event.show_externally))
      .filter((group) => group.length > 0);

    if (!groups.length) {
      return <Empty description={t('No tracking events available')} />;
    }

    return (
      <Timeline mode="left" className={classes.timeline}>
        {groups.map((group, groupIndex) => {
          const head = group[0];
          const isGreen = isDeliveredStatus(head.status_internal);
          const isCheck = isGreen || head.status_internal === 'pickup_completed';
          const icon =
            head.status_internal === 'softdata_upload' ? (
              <UploadOutlined />
            ) : isCheck ? (
              <CheckOutlined />
            ) : (
              <TruckOutlined />
            );
          const heading =
            groupedStatusBy === 'status_internal' ? head.status_internal : head.status_external;

          return (
            <Timeline.Item
              key={`group-${groupIndex}`}
              label={renderLabel(head.timestamp)}
              dot={renderDot(isGreen, icon)}
            >
              <div className={classes.content}>
                <Typography.Text className={classes.eventTitle}>
                  {prettifyStatus(heading)}
                </Typography.Text>
                {group.map((event, eventIndex) => {
                  const detail =
                    groupedStatusBy === 'status_internal'
                      ? event.status_external
                      : event.status_internal;
                  const location = buildLocationText(event);
                  return (
                    <div key={`group-${groupIndex}-${eventIndex}`} className={classes.groupItem}>
                      {location ? (
                        <Typography.Text type="secondary">{location}</Typography.Text>
                      ) : null}
                      <Typography.Text type="secondary">
                        {`${prettifyStatus(detail)}: ${resolveEventDescription(event, t, i18n.language)}`}
                      </Typography.Text>
                      {renderFailureReason(event)}
                      {event.timestamp ? (
                        <Typography.Text className={classes.time}>
                          {`${customFormatDate(event.timestamp, DATE_FORMAT)}, ${customFormatDate(
                            event.timestamp,
                            TIME_FORMAT,
                          )}`}
                        </Typography.Text>
                      ) : null}
                    </div>
                  );
                })}
                {renderReceiverInfo(head.status_internal)}
              </div>
            </Timeline.Item>
          );
        })}
      </Timeline>
    );
  }

  const events = consignment.trackingHistory || [];
  if (!events.length) {
    return <Empty description={t('No tracking events available')} />;
  }

  return (
    <Timeline mode="left" className={classes.timeline}>
      {events.map((event, index) => {
        const isGreen = event.status_internal === 'delivered' || index > 0;
        const icon = isGreen ? <CheckOutlined /> : <TruckOutlined />;
        const location = buildLocationText(event);
        const showExternal =
          event.status_external && event.status_external !== event.status_internal;

        return (
          <Timeline.Item
            key={`event-${index}`}
            label={renderLabel(event.timestamp)}
            dot={renderDot(isGreen, icon)}
          >
            <div className={classes.content}>
              <Typography.Text className={classes.eventTitle}>
                {resolveEventDescription(event, t, i18n.language) || '-'}
              </Typography.Text>
              {location ? <Typography.Text type="secondary">{location}</Typography.Text> : null}
              {showExternal ? (
                <Typography.Text type="secondary">{event.status_external}</Typography.Text>
              ) : null}
              {renderFailureReason(event)}
              {renderReceiverInfo(event.status_internal)}
            </div>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
};

export default TrackingTimeline;
